import { t } from "@travily/i18n";
import type DestinationsShow from "@travily/types/pages/DestinationsShow";
import { Image } from "@ui/components/Image";
import { SectionJumpBar } from "@ui/components/SectionJumpBar";
import { SectionJumpNav, type SectionJumpSection } from "@ui/components/SectionJumpNav";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { useScrollSpy } from "@ui/hooks/use-scroll-spy";
import { tv } from "@ui/lib/tv";
import { useCallback, useRef } from "react";
import type { Household } from "./apply-household";
import { CostsSection } from "./components/costs/CostsSection";

/** Gap kept between the floating jump bar and the section scrolled to it. */
const JUMP_BAR_SCROLL_GAP = 16;
/** Jump offset while the floating bar hasn't rendered yet (top-4 + bar + gap). */
const JUMP_BAR_FALLBACK_OFFSET = 108;

const showView = tv({
  slots: {
    root: "flex flex-col",
    gallery: "grid grid-cols-2 gap-2 md:grid-cols-4",
    photo: "aspect-square overflow-hidden rounded-2xl",
    name: "mt-6 font-extrabold text-[30px] text-neutral-900 leading-10 tracking-[-0.3px] xl:text-display-md",
    country: "mt-1 text-lg text-neutral-600",
    sentence: "mt-3 max-w-2xl text-base text-neutral-900 leading-6",
    jumpNav: "sticky top-0 z-(--z-chrome-top) -mx-5 mt-6 md:hidden",
    jumpBarSticky: "sticky top-4 z-30 mt-6 hidden h-0 md:block",
    // Jump targets: focused programmatically after a jump, so no focus ring.
    section: "outline-none",
  },
});

export interface ShowViewProps {
  destination: DestinationsShow["destination"];
  costs: DestinationsShow["costs"];
  /** The household the reader asked for — the server's answer while idle, the
   *  pending one while a re-quote is in flight. */
  household: Household;
  onHouseholdChange: (household: Household) => void;
  isQuoting?: boolean;
}

/**
 * The destination page as it stands: a minimal hero and the costs panel.
 *
 * Climate, community and the rest are not stubbed here on purpose — a section
 * that announces itself and then says nothing is worse than one that has not
 * arrived, and the jump nav lists exactly what the page can answer today.
 */
export function ShowView({ costs, destination, household, isQuoting = false, onHouseholdChange }: ShowViewProps) {
  const styles = showView();
  const isDesktop = useBreakpoint("md");

  const sections: SectionJumpSection[] = [
    { key: "overview", label: t("components.views.destinations.show.sections.overview") },
    ...(costs ? [{ key: "costs", label: t("components.views.destinations.show.sections.costs") }] : []),
  ];

  const jumpNavRef = useRef<HTMLDivElement>(null);
  const jumpBarRef = useRef<HTMLDivElement>(null);
  const getTopOffset = useCallback(() => {
    const strip = jumpNavRef.current;
    if (strip?.offsetParent) return strip.getBoundingClientRect().height;
    const bar = jumpBarRef.current;
    if (bar) return bar.getBoundingClientRect().bottom + JUMP_BAR_SCROLL_GAP;
    return JUMP_BAR_FALLBACK_OFFSET;
  }, []);
  const { activeKey, pastFirstSection, registerSection, scrollToSection } = useScrollSpy({
    keys: sections.map(({ key }) => key),
    getTopOffset,
  });

  return (
    <div className={styles.root()}>
      {destination.photos.length > 0 && (
        <div className={styles.gallery()}>
          {destination.photos.slice(0, 4).map((photo) => (
            <div className={styles.photo()} key={photo.id}>
              <Image className="size-full object-cover" image={photo.image} sizes="(min-width: 768px) 25vw, 50vw" />
            </div>
          ))}
        </div>
      )}

      <section className={styles.section()} ref={registerSection("overview")} tabIndex={-1}>
        <h1 className={styles.name()}>{destination.name}</h1>
        <p className={styles.country()}>{destination.country_name}</p>
        {destination.marketing_sentence && <p className={styles.sentence()}>{destination.marketing_sentence}</p>}
      </section>

      {sections.length > 1 && (
        <>
          <div className={styles.jumpBarSticky()}>
            {isDesktop && pastFirstSection && (
              <div ref={jumpBarRef}>
                <SectionJumpBar
                  activeKey={activeKey}
                  onSelect={scrollToSection}
                  sections={sections}
                  title={destination.name}
                />
              </div>
            )}
          </div>
          <div className={styles.jumpNav()} ref={jumpNavRef}>
            <SectionJumpNav activeKey={activeKey} onSelect={scrollToSection} sections={sections} />
          </div>
        </>
      )}

      {costs && (
        <section className={styles.section({ className: "mt-10" })} ref={registerSection("costs")} tabIndex={-1}>
          <CostsSection
            costs={costs}
            household={household}
            isPending={isQuoting}
            onHouseholdChange={onHouseholdChange}
          />
        </section>
      )}
    </div>
  );
}
